import type { JSONContent } from "@tiptap/core"
import { Fragment, useMemo, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { MENTION_CATEGORY_CLASS } from "./mention-node"
import { resolveCamLabel } from "./prompt-mention-display"
import { textToDoc } from "./serialize"
import { mentionDisplayLabel, type MentionCategory, type MentionItem } from "./types"

export { mentionDisplayLabelsInPromptText, resolveCamLabel } from "./prompt-mention-display"

type Props = {
  text: string
  items: MentionItem[]
  className?: string
}

function mentionNodeKey(attrs: Record<string, unknown> | undefined, index: number): string {
  const category = String(attrs?.category ?? "reference")
  const id = String(attrs?.id ?? 0)
  const stateId = attrs?.stateId == null || attrs.stateId === "" ? "" : String(attrs.stateId)
  const attrKey = attrs?.attrKey == null || attrs.attrKey === "" ? "" : String(attrs.attrKey)
  return `${category}:${id}:${stateId}:${attrKey}:${index}`
}

function renderMentionChip(category: MentionCategory, label: string, key: string): ReactNode {
  const shown = mentionDisplayLabel(category, label)
  return (
    <span key={key} className={MENTION_CATEGORY_CLASS[category] ?? "pe-mention"} data-category={category}>
      <span className="pe-mention__label">{shown}</span>
    </span>
  )
}

function renderInline(nodes: JSONContent[] | undefined): ReactNode {
  if (!nodes?.length) return null
  return nodes.map((node, index) => {
    if (node.type === "text") {
      return <Fragment key={`t-${index}`}>{node.text ?? ""}</Fragment>
    }
    if (node.type === "mention") {
      const category = (node.attrs?.category ?? "reference") as MentionCategory
      const label = String(node.attrs?.label ?? "")
      return renderMentionChip(
        category,
        label,
        mentionNodeKey(node.attrs as Record<string, unknown> | undefined, index),
      )
    }
    return null
  })
}

function renderBlock(node: JSONContent, index: number, items: MentionItem[]): ReactNode {
  if (node.type === "paragraph") {
    return (
      <Fragment key={`p-${index}`}>
        {index > 0 ? "\n" : null}
        {renderInline(node.content)}
      </Fragment>
    )
  }
  if (node.type === "promptBlock") {
    const tc = String(node.attrs?.timecode ?? "0-1 sec")
    const cameraPresetIdRaw = node.attrs?.cameraPresetId
    const cameraId = cameraPresetIdRaw != null && cameraPresetIdRaw !== "" ? Number(cameraPresetIdRaw) : null
    const paras = (node.content ?? []).filter((c) => c.type === "paragraph")
    return (
      <Fragment key={`b-${index}`}>
        {index > 0 ? "\n" : null}
        {`[${tc}]`}
        {cameraId != null && Number.isFinite(cameraId)
          ? ["\n", renderMentionChip("cam", resolveCamLabel(items, cameraId), `cam-${index}-${cameraId}`)]
          : null}
        {paras.map((para, paraIndex) => (
          <Fragment key={`bp-${index}-${paraIndex}`}>
            {"\n"}
            {renderInline(para.content)}
          </Fragment>
        ))}
      </Fragment>
    )
  }
  return null
}

/** Read-only prompt text with mention chips resolved via the same catalog as PromptEditor. */
export function PromptTextWithMentions({ text, items, className }: Props) {
  const doc = useMemo(() => textToDoc(text, items), [text, items])
  const blocks = doc.content ?? []

  return (
    <div
      className={cn(
        "it2tv-scrollbar max-h-48 overflow-auto px-2.5 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-ui-text-primary",
        className,
      )}
    >
      {blocks.map((node, index) => renderBlock(node, index, items))}
    </div>
  )
}
